import type { CognitoGroup } from '@respaldos/shared';
import { COGNITO_GROUPS } from '@respaldos/shared';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getAppConfig, isAppConfigured } from './config';
import type { SessionUser } from './types';

export interface AdminUser {
  username: string;
  email: string;
  given_name?: string;
  family_name?: string;
  name?: string;
  sub: string;
  group: CognitoGroup | null;
  groups: string[];
  status: 'pending' | 'active' | 'disabled' | 'unknown';
  enabled: boolean;
  userStatus: string;
  delivery?: 'email' | 'local';
}

export { COGNITO_GROUPS };

async function authHeaders(): Promise<Record<string, string>> {
  if (!isAppConfigured()) {
    throw new Error('Configuración incompleta: faltan variables Cognito o API.');
  }
  const auth = await fetchAuthSession();
  const token = auth.tokens?.idToken?.toString();
  if (!token) {
    throw new Error('Sesión Cognito expirada. Vuelva a iniciar sesión.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function apiFetch(path: string, init?: RequestInit) {
  const base = getAppConfig().apiUrl;
  const headers = await authHeaders();
  const res = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      ...headers,
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Error API ${res.status}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

export async function listAdminUsers(_session: SessionUser): Promise<AdminUser[]> {
  const res = await apiFetch('/admin/users');
  return res.users;
}

export async function inviteAdminUser(
  _session: SessionUser,
  email: string,
  group: CognitoGroup,
  names: { given_name: string; family_name: string },
): Promise<AdminUser & { delivery: string }> {
  return apiFetch('/admin/users', {
    method: 'POST',
    body: JSON.stringify({
      email,
      group,
      given_name: names.given_name,
      family_name: names.family_name,
    }),
  });
}

export async function resendAdminInvite(
  _session: SessionUser,
  username: string,
): Promise<AdminUser> {
  return apiFetch(`/admin/users/${encodeURIComponent(username)}/resend`, {
    method: 'POST',
  });
}

export async function setAdminUserGroup(
  _session: SessionUser,
  username: string,
  group: CognitoGroup,
): Promise<AdminUser> {
  return apiFetch(`/admin/users/${encodeURIComponent(username)}/group`, {
    method: 'PUT',
    body: JSON.stringify({ group }),
  });
}

export async function setAdminUserEnabled(
  _session: SessionUser,
  username: string,
  enabled: boolean,
): Promise<AdminUser> {
  const action = enabled ? 'enable' : 'disable';
  return apiFetch(`/admin/users/${encodeURIComponent(username)}/${action}`, {
    method: 'POST',
  });
}

export const GROUP_LABELS: Record<CognitoGroup, string> = {
  Marketing: 'Marketing',
  BackupsBO: 'Respaldos · Bolivia',
  BackupsPE: 'Respaldos · Perú',
  BackupsPY: 'Respaldos · Paraguay',
  Admins: 'Administradores',
};
